import React, { useEffect, useMemo, useState } from "react";
import {
  ApprovalPendingItem,
  decideApproval,
  getApprovalsPending,
} from "../../../../api/approvals";
import "./ApprovalsPage.css";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; items: ApprovalPendingItem[] }
  | { kind: "error"; message: string };

type BadgeTone = "success" | "danger" | "warning" | "neutral" | "info";

function fmtWhen(val: string | null | undefined) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

function fmtText(val: unknown) {
  const s = String(val ?? "").trim();
  return s.length ? s : "—";
}

function getLoose(it: ApprovalPendingItem, key: string) {
  return (it as any)?.[key];
}

function getLooseString(it: ApprovalPendingItem, key: string) {
  return String(getLoose(it, key) ?? "").trim();
}

function hasValue(val: unknown) {
  return String(val ?? "").trim().length > 0;
}

function truthyDbValue(val: unknown) {
  if (val === true) return true;
  if (val === 1) return true;
  const s = String(val ?? "").trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

function renderExistingClinicSummary(it: ApprovalPendingItem) {
  const name = fmtText(it.existingClinicName);
  const addr1 = fmtText(it.existingClinicAddressLine1);
  const addr2 = String(it.existingClinicAddressLine2 || "").trim();
  const city = fmtText(it.existingClinicCityName);
  const postal = String(it.existingClinicPostalCode || "").trim();
  const phone = String(it.existingClinicPhone || "").trim();
  const website = String(it.existingClinicWebsite || "").trim();

  const parts: string[] = [];
  if (name !== "—") parts.push(name);

  const addrParts: string[] = [];
  if (addr1 !== "—") addrParts.push(addr1);
  if (addr2) addrParts.push(addr2);
  const addrLine = addrParts.join(", ");

  const cityLine = [city !== "—" ? city : "", postal ? `(${postal})` : ""]
    .filter(Boolean)
    .join(" ");

  if (addrLine) parts.push(addrLine);
  if (cityLine) parts.push(cityLine);
  if (phone) parts.push(phone);
  if (website) parts.push(website);

  return parts.length ? parts.join(" • ") : "—";
}

function renderNewClinicSummary(it: ApprovalPendingItem) {
  const name = fmtText(it.newClinicName);
  const addr1 = fmtText(it.newClinicAddressLine1);
  const addr2 = String(it.newClinicAddressLine2 || "").trim();
  const city = fmtText(it.newClinicCityName);
  const province = getLooseString(it, "newClinicProvince");
  const postal = String(it.newClinicPostalCode || "").trim();
  const phone = String(it.newClinicPhone || "").trim();
  const website = String(it.newClinicWebsite || "").trim();

  const parts: string[] = [];
  if (name !== "—") parts.push(name);

  const addrParts: string[] = [];
  if (addr1 !== "—") addrParts.push(addr1);
  if (addr2) addrParts.push(addr2);
  const addrLine = addrParts.join(", ");

  const cityLine = [
    city !== "—" ? city : "",
    province || "",
    postal ? `(${postal})` : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (addrLine) parts.push(addrLine);
  if (cityLine) parts.push(cityLine);
  if (phone) parts.push(phone);
  if (website) parts.push(website);

  return parts.length ? parts.join(" • ") : "—";
}

function clinicAdminActionLabel(action: string) {
  const a = String(action || "").trim();
  if (a === "Add") return "Add Clinic Admin";
  if (a === "Replace") return "Replace Clinic Admin(s)";
  return a || "—";
}

function badgeToneStyles(tone: BadgeTone) {
  const styles: Record<
    BadgeTone,
    { bg: string; bd: string; fg: string; dot: string }
  > = {
    success: {
      bg: "rgba(22, 163, 74, 0.10)",
      bd: "rgba(22, 163, 74, 0.25)",
      fg: "#166534",
      dot: "#16a34a",
    },
    danger: {
      bg: "rgba(185, 28, 28, 0.08)",
      bd: "rgba(185, 28, 28, 0.22)",
      fg: "#991b1b",
      dot: "#dc2626",
    },
    warning: {
      bg: "#fffaf0",
      bd: "#f4d18f",
      fg: "#92400e",
      dot: "#b45309",
    },
    neutral: {
      bg: "#f8fafc",
      bd: "#e2e8f0",
      fg: "#64748b",
      dot: "#94a3b8",
    },
    info: {
      bg: "#eaf4ff",
      bd: "#cfe6ff",
      fg: "#1669b3",
      dot: "#1669b3",
    },
  };

  return styles[tone];
}

function PillBadge({
  label,
  tone = "neutral",
  title,
}: {
  label: string;
  tone?: BadgeTone;
  title?: string;
}) {
  const s = badgeToneStyles(tone);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 8px",
        borderRadius: 999,
        border: `1px solid ${s.bd}`,
        background: s.bg,
        color: s.fg,
        fontSize: 12,
        fontWeight: 750,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
      }}
      title={title || label}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: 999,
          background: s.dot,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function ynBadge(label: string, ok: boolean | null) {
  return (
    <PillBadge
      label={`${label}: ${ok ? "Yes" : "No"}`}
      tone={ok ? "success" : "danger"}
      title={`${label}: ${ok ? "Yes" : "No"}`}
    />
  );
}

function getGovernanceStatusTone(status: string): BadgeTone {
  const s = status.trim().toLowerCase();
  if (s === "approved") return "success";
  if (s === "pending") return "warning";
  if (s === "suspended" || s === "disabled" || s === "rejected") {
    return "danger";
  }
  return "neutral";
}

function getNewClinicReadiness(it: ApprovalPendingItem) {
  const missing: string[] = [];

  if (!hasValue(it.newClinicName)) missing.push("Name");
  if (!hasValue(it.newClinicAddressLine1)) missing.push("Address");
  if (!hasValue(it.newClinicCityId)) missing.push("City");
  if (!hasValue(it.newClinicPostalCode)) missing.push("Postal code");
  if (!hasValue(it.newClinicPhone)) missing.push("Phone");

  return missing;
}

function getLegalSignals(it: ApprovalPendingItem) {
  const attestationAccepted = truthyDbValue(getLoose(it, "attestationAccepted"));
  const attestationAcceptedAt =
    getLoose(it, "attestationAcceptedAt") ||
    getLoose(it, "AttestationAcceptedAt");

  const termsAccepted = truthyDbValue(getLoose(it, "termsAccepted"));
  const termsAcceptedAt =
    getLoose(it, "termsAcceptedAt") || getLoose(it, "TermsAcceptedAt");

  const agreementAccepted = truthyDbValue(
    getLoose(it, "clinicAdminAgreementAccepted")
  );
  const agreementAcceptedAt =
    getLoose(it, "clinicAdminAgreementAcceptedAt") ||
    getLoose(it, "ClinicAdminAgreementAcceptedAt");

  const anyLegalSignal =
    getLoose(it, "attestationAccepted") !== undefined ||
    getLoose(it, "attestationAcceptedAt") !== undefined ||
    getLoose(it, "AttestationAcceptedAt") !== undefined ||
    getLoose(it, "termsAccepted") !== undefined ||
    getLoose(it, "termsAcceptedAt") !== undefined ||
    getLoose(it, "TermsAcceptedAt") !== undefined ||
    getLoose(it, "clinicAdminAgreementAccepted") !== undefined ||
    getLoose(it, "clinicAdminAgreementAcceptedAt") !== undefined ||
    getLoose(it, "ClinicAdminAgreementAcceptedAt") !== undefined;

  return {
    anyLegalSignal,
    attestationOk: Boolean(attestationAccepted || attestationAcceptedAt),
    termsOk: Boolean(termsAccepted || termsAcceptedAt),
    agreementOk: Boolean(agreementAccepted || agreementAcceptedAt),
    attestationAcceptedAt: attestationAcceptedAt
      ? fmtWhen(String(attestationAcceptedAt))
      : null,
    termsAcceptedAt: termsAcceptedAt ? fmtWhen(String(termsAcceptedAt)) : null,
    agreementAcceptedAt: agreementAcceptedAt
      ? fmtWhen(String(agreementAcceptedAt))
      : null,
  };
}

/**
 * Super Admin – Approvals Page
 *
 * Purpose:
 * - SuperAdmin-only inbox for pending ClinicAdmin access requests.
 * - Two cases:
 *   (A) Existing clinic request: ClinicID is set + ClinicAdminAction is Add/Replace
 *   (B) New clinic request: ClinicID is null + draft clinic fields on AccessRequest
 *
 * Implementation notes:
 * - Uses /api/approvals-pending + POST /api/approvals/{id}/decide
 * - API decision values sent are "approve" | "decline" (backend maps decline -> Rejected)
 *
 * Guardrails:
 * - Self-requests may appear in the inbox, but are NOT actionable.
 * - Decision payload stays minimal.
 * - Legal/attestation metadata is displayed when present.
 * - No patient PHI is shown on this page.
 */
export default function ApprovalsPage() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busyId, setBusyId] = useState<number | null>(null);
  const [noteById, setNoteById] = useState<Record<number, string>>({});

  async function load() {
    setState({ kind: "loading" });
    try {
      const res = await getApprovalsPending();
      setState({ kind: "ready", items: res.items || [] });
    } catch (e: unknown) {
      const msg = (e as any)?.message || "Failed to load pending approvals.";
      setState({ kind: "error", message: msg });
    }
  }

  useEffect(() => {
    load();
  }, []);

  const items = useMemo(
    () => (state.kind === "ready" ? state.items : []),
    [state]
  );

  const selfRequestCount = useMemo(
    () => items.filter((it) => Boolean((it as any).isSelfRequest)).length,
    [items]
  );

  const missingLegalCount = useMemo(
    () =>
      items.filter((it) => {
        const legal = getLegalSignals(it);
        return (
          legal.anyLegalSignal &&
          (!legal.attestationOk || !legal.termsOk || !legal.agreementOk)
        );
      }).length,
    [items]
  );

  async function onDecide(
    accessRequestId: number,
    decision: "approve" | "decline",
    isSelfRequest: boolean
  ) {
    if (busyId) return;

    if (isSelfRequest) {
      // eslint-disable-next-line no-alert
      alert("You cannot approve or reject your own request.");
      return;
    }

    const note = String(noteById[accessRequestId] || "").trim();

    if (decision === "decline") {
      const ok = window.confirm(
        note
          ? "Reject this request?"
          : "Reject this request without a decision note?"
      );
      if (!ok) return;
    }

    setBusyId(accessRequestId);

    try {
      await decideApproval(accessRequestId, decision, note);
      await load();
    } catch (e: unknown) {
      // eslint-disable-next-line no-alert
      alert((e as any)?.message || "Decision failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="approvals-page">
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div style={{ minWidth: 280 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "5px 10px",
              borderRadius: 999,
              background: "#eaf4ff",
              border: "1px solid #cfe6ff",
              color: "#1669b3",
              fontSize: 12,
              fontWeight: 650,
              marginBottom: 10,
            }}
          >
            SuperAdmin-only governance
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 650,
              color: "#102a43",
              letterSpacing: "-0.02em",
            }}
          >
            Approvals
          </h1>

          <p
            style={{
              marginTop: 8,
              color: "#6b7a94",
              lineHeight: 1.55,
              maxWidth: 860,
              fontSize: 14,
              fontWeight: 450,
            }}
          >
            Review pending Clinic Admin access requests. Approving a new clinic
            creates the clinic, grants Clinic Admin access, and writes governance
            audit events. Self-approval remains blocked.
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          disabled={state.kind === "loading"}
          style={{
            padding: "9px 12px",
            borderRadius: 12,
            border: "1px solid #d1d9e6",
            background: "#ffffff",
            color: "#102a43",
            fontWeight: 650,
            cursor: state.kind === "loading" ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
            opacity: state.kind === "loading" ? 0.65 : 1,
          }}
        >
          {state.kind === "loading" ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {state.kind === "ready" ? (
        <section
          style={{
            padding: 16,
            borderRadius: 18,
            border:
              items.length > 0 ? "1px solid #b9dcff" : "1px solid #d1d9e6",
            background: items.length > 0 ? "#f7fbff" : "#ffffff",
            boxShadow: "0 8px 24px rgba(15, 80, 135, 0.04)",
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <PillBadge
              label={`${items.length} pending`}
              tone={items.length > 0 ? "info" : "neutral"}
            />

            <PillBadge
              label={`${selfRequestCount} self-request${selfRequestCount === 1 ? "" : "s"}`}
              tone={selfRequestCount > 0 ? "warning" : "neutral"}
            />

            <PillBadge
              label={`${missingLegalCount} legal flag${missingLegalCount === 1 ? "" : "s"}`}
              tone={missingLegalCount > 0 ? "warning" : "neutral"}
            />

            <PillBadge label="No patient PHI" tone="success" />
          </div>

          <div
            style={{
              marginTop: 10,
              color: "#6b7a94",
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 450,
              maxWidth: 900,
            }}
          >
            This inbox is for clinic and portal-access governance only. Patient
            request details and clinical information do not belong here.
          </div>
        </section>
      ) : null}

      {state.kind === "loading" ? (
        <div className="approvals-card" style={{ color: "#64748b" }}>
          Loading pending approvals…
        </div>
      ) : null}

      {state.kind === "error" ? (
        <div
          className="approvals-card"
          style={{ borderColor: "rgba(185, 28, 28, 0.35)" }}
        >
          <div style={{ fontWeight: 800, color: "#991b1b" }}>
            Could not load approvals
          </div>
          <div style={{ marginTop: 6, color: "#64748b" }}>{state.message}</div>
        </div>
      ) : null}

      {state.kind === "ready" ? (
        <div className="approvals-card">
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            <div className="approvals-title">Pending Clinic Admin Requests</div>

            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700 }}>
              {items.length} item{items.length === 1 ? "" : "s"}
            </div>
          </div>

          {items.length === 0 ? (
            <div className="approvals-muted">No pending requests.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: 1180,
                }}
              >
                <thead>
                  <tr>
                    <Th>Requester</Th>
                    <Th>Request type</Th>
                    <Th>Clinic</Th>
                    <Th>Access checks</Th>
                    <Th>Submitted</Th>
                    <Th width={320}>Decision note</Th>
                    <Th align="right" width={240}>
                      Actions
                    </Th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((it) => {
                    const accessRequestId = it.accessRequestId;
                    const isBusy = busyId === accessRequestId;

                    const isExistingClinicRequest = Boolean(it.clinicId);
                    const action = String(it.clinicAdminAction || "").trim();

                    const requestType = isExistingClinicRequest
                      ? clinicAdminActionLabel(action)
                      : "Create new clinic";

                    const isSelfRequest = Boolean((it as any).isSelfRequest);

                    const actionsDisabled = Boolean(busyId) || isSelfRequest;

                    const disabledReason = isSelfRequest
                      ? "You cannot approve your own request."
                      : "";

                    const requesterFirstName = getLooseString(
                      it,
                      "requesterFirstName"
                    );
                    const requesterLastName = getLooseString(
                      it,
                      "requesterLastName"
                    );
                    const requesterPhone = getLooseString(it, "requesterPhone");

                    const requesterFullName = [
                      requesterFirstName,
                      requesterLastName,
                    ]
                      .filter(Boolean)
                      .join(" ")
                      .trim();

                    const legal = getLegalSignals(it);

                    const existingGovernanceStatus = getLooseString(
                      it,
                      "existingClinicGovernanceStatus"
                    );

                    const missingDraftFields = isExistingClinicRequest
                      ? []
                      : getNewClinicReadiness(it);

                    const newClinicReady = missingDraftFields.length === 0;

                    return (
                      <tr key={accessRequestId}>
                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                          }}
                        >
                          <div style={{ fontWeight: 750, color: "#102a43" }}>
                            {it.displayName || "—"}
                          </div>

                          <div style={{ color: "#64748b", fontSize: 13 }}>
                            {it.email || "—"}
                          </div>

                          {requesterFullName ? (
                            <div
                              style={{
                                marginTop: 6,
                                color: "#102a43",
                                fontSize: 13,
                              }}
                            >
                              <span style={{ fontWeight: 750 }}>Requester:</span>{" "}
                              {requesterFullName}
                            </div>
                          ) : null}

                          {requesterPhone ? (
                            <div
                              style={{
                                marginTop: 2,
                                color: "#64748b",
                                fontSize: 13,
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: 750,
                                  color: "#64748b",
                                }}
                              >
                                Phone:
                              </span>{" "}
                              {requesterPhone}
                            </div>
                          ) : null}

                          <div
                            style={{
                              marginTop: 8,
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            <PillBadge
                              label={`Req #${accessRequestId}`}
                              tone="neutral"
                            />
                            {isSelfRequest ? (
                              <PillBadge label="Your request" tone="warning" />
                            ) : null}
                          </div>
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                            fontWeight: 750,
                            whiteSpace: "nowrap",
                            color: "#102a43",
                          }}
                        >
                          {requestType}

                          {isExistingClinicRequest &&
                          action.toLowerCase() === "replace" ? (
                            <div
                              style={{
                                marginTop: 8,
                                color: "#92400e",
                                fontSize: 12,
                                fontWeight: 650,
                                lineHeight: 1.4,
                              }}
                            >
                              Replaces existing active Clinic Admin role(s).
                            </div>
                          ) : null}
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                            minWidth: 300,
                          }}
                        >
                          {isExistingClinicRequest ? (
                            <div>
                              <div style={{ fontWeight: 750, color: "#102a43" }}>
                                ClinicID {it.clinicId ?? "—"}
                              </div>

                              <div
                                style={{
                                  color: "#64748b",
                                  fontSize: 13,
                                  marginTop: 4,
                                  lineHeight: 1.45,
                                }}
                              >
                                {renderExistingClinicSummary(it)}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginTop: 8,
                                }}
                              >
                                {existingGovernanceStatus ? (
                                  <PillBadge
                                    label={`Governance: ${existingGovernanceStatus}`}
                                    tone={getGovernanceStatusTone(
                                      existingGovernanceStatus
                                    )}
                                  />
                                ) : null}

                                {typeof it.existingClinicActiveAdminCount ===
                                "number" ? (
                                  <PillBadge
                                    label={`Active admins: ${it.existingClinicActiveAdminCount}`}
                                    tone={
                                      it.existingClinicActiveAdminCount > 0
                                        ? "success"
                                        : "warning"
                                    }
                                  />
                                ) : null}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontWeight: 750, color: "#102a43" }}>
                                New clinic draft
                              </div>

                              <div
                                style={{
                                  color: "#64748b",
                                  fontSize: 13,
                                  marginTop: 4,
                                  lineHeight: 1.45,
                                }}
                              >
                                {renderNewClinicSummary(it)}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  flexWrap: "wrap",
                                  marginTop: 8,
                                }}
                              >
                                <PillBadge
                                  label={
                                    newClinicReady
                                      ? "Draft complete"
                                      : `Missing: ${missingDraftFields.join(", ")}`
                                  }
                                  tone={newClinicReady ? "success" : "warning"}
                                />
                              </div>
                            </div>
                          )}
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                            minWidth: 240,
                          }}
                        >
                          {legal.anyLegalSignal ? (
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              {ynBadge("Attestation", legal.attestationOk)}
                              {ynBadge("Terms", legal.termsOk)}
                              {ynBadge("Agreement", legal.agreementOk)}

                              {legal.attestationAcceptedAt ? (
                                <SmallMeta>
                                  Attestation: {legal.attestationAcceptedAt}
                                </SmallMeta>
                              ) : null}

                              {legal.termsAcceptedAt ? (
                                <SmallMeta>Terms: {legal.termsAcceptedAt}</SmallMeta>
                              ) : null}

                              {legal.agreementAcceptedAt ? (
                                <SmallMeta>
                                  Agreement: {legal.agreementAcceptedAt}
                                </SmallMeta>
                              ) : null}
                            </div>
                          ) : (
                            <div style={{ color: "#94a3b8", fontSize: 13 }}>
                              Legal acceptance metadata not available.
                            </div>
                          )}
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                            whiteSpace: "nowrap",
                            color: "#102a43",
                            fontWeight: 650,
                          }}
                        >
                          {fmtWhen(it.submittedAt)}
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                          }}
                        >
                          <textarea
                            value={noteById[accessRequestId] || ""}
                            maxLength={1000}
                            onChange={(e) => {
                              setNoteById((prev) => ({
                                ...prev,
                                [accessRequestId]: e.target.value,
                              }));
                            }}
                            placeholder={
                              isSelfRequest
                                ? "Not applicable for your own request"
                                : "Optional note. Strongly recommended for rejection."
                            }
                            disabled={isSelfRequest}
                            style={{
                              width: "100%",
                              minHeight: 74,
                              resize: "vertical",
                              borderRadius: 12,
                              border: "1px solid #d1d9e6",
                              padding: "10px 10px",
                              fontFamily: "inherit",
                              fontSize: 13,
                              outline: "none",
                              background: isSelfRequest ? "#f8fafc" : "#ffffff",
                              color: isSelfRequest ? "#94a3b8" : "#102a43",
                              boxSizing: "border-box",
                            }}
                          />

                          <div
                            style={{
                              marginTop: 5,
                              color: "#94a3b8",
                              fontSize: 12,
                              fontWeight: 600,
                              textAlign: "right",
                            }}
                          >
                            {(noteById[accessRequestId] || "").length}/1000
                          </div>
                        </td>

                        <td
                          style={{
                            padding: "12px 8px",
                            borderBottom: "1px solid #f0f4fa",
                            verticalAlign: "top",
                            textAlign: "right",
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              gap: 8,
                              alignItems: "center",
                              justifyContent: "flex-end",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              type="button"
                              disabled={actionsDisabled}
                              title={disabledReason}
                              onClick={() =>
                                onDecide(
                                  accessRequestId,
                                  "approve",
                                  isSelfRequest
                                )
                              }
                              style={{
                                padding: "10px 12px",
                                borderRadius: 12,
                                border: "1px solid #0f5087",
                                background: "#1669b3",
                                color: "#fff",
                                fontWeight: 700,
                                cursor: actionsDisabled
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: actionsDisabled ? 0.55 : 1,
                              }}
                            >
                              {isBusy ? "Working…" : "Approve"}
                            </button>

                            <button
                              type="button"
                              disabled={actionsDisabled}
                              title={disabledReason}
                              onClick={() =>
                                onDecide(
                                  accessRequestId,
                                  "decline",
                                  isSelfRequest
                                )
                              }
                              style={{
                                padding: "10px 12px",
                                borderRadius: 12,
                                border: "1px solid rgba(185, 28, 28, 0.45)",
                                background: "rgba(185, 28, 28, 0.06)",
                                color: "#991b1b",
                                fontWeight: 700,
                                cursor: actionsDisabled
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: actionsDisabled ? 0.55 : 1,
                              }}
                            >
                              {isBusy ? "Working…" : "Reject"}
                            </button>

                            {isSelfRequest ? (
                              <div
                                style={{
                                  width: "100%",
                                  marginTop: 6,
                                  fontSize: 12,
                                  color: "#94a3b8",
                                  fontWeight: 700,
                                  textAlign: "right",
                                }}
                              >
                                You cannot approve your own request.
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/* ---------- Local table/UI components ---------- */

function Th({
  children,
  align = "left",
  width,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  width?: number;
}) {
  return (
    <th
      style={{
        textAlign: align,
        fontSize: 12,
        color: "#64748b",
        padding: "10px 8px",
        borderBottom: "1px solid #e6edf7",
        whiteSpace: "nowrap",
        width,
      }}
    >
      {children}
    </th>
  );
}

function SmallMeta({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        width: "100%",
        color: "#94a3b8",
        fontSize: 12,
        fontWeight: 650,
        lineHeight: 1.35,
      }}
    >
      {children}
    </div>
  );
}